import React, { useState, useEffect } from 'react';
import Icon from 'src/components/common/maintain-table/bootstrap/Icon';
import ActionButton from 'src/components/visionx/buttons/ActionButton';

const TranscriptDisplay: React.FC<React.PropsWithChildren<unknown>> = () => {
  const [transcript, setTranscript] = useState<string>('');
  const [isListening, setIsListening] = useState<boolean>(false);
  const [displayClass, setDisplayClass] = useState<string>('');
  const [browserSupportsSpeechRecognition, setBrowserSupportsSpeechRecognition] =
    useState<boolean>(true);
  const [isMicrophoneAvailable, setIsMicrophoneAvailable] = useState<boolean>(true);

  useEffect(() => {
    const handleTranscriptUpdate = (event: CustomEvent<{ transcript: string }>) => {
      setTranscript(event.detail.transcript);
      setIsListening(true);
      setDisplayClass('fade-in');
    };

    const handleTranscriptStop = () => {
      setDisplayClass('fade-out');
      setTimeout(() => {
        setIsListening(false);
        setTranscript('');
      }, 500);
    };

    const handleBrowserSupportsSpeechRecognition = (
      event: CustomEvent<{ browserSupportsSpeechRecognition: boolean }>,
    ) => {
      setBrowserSupportsSpeechRecognition(event.detail.browserSupportsSpeechRecognition);
    };

    const handleMicrophoneAvailability = (
      event: CustomEvent<{ isMicrophoneAvailable: boolean }>,
    ) => {
      setIsMicrophoneAvailable(event.detail.isMicrophoneAvailable);
    };
    const handleAwaitingPermission = (event: CustomEvent<{ isAwaitingPermission: boolean }>) => {
      setIsMicrophoneAvailable(event.detail.isAwaitingPermission);
    };

    window.addEventListener('onTranscriptUpdate', handleTranscriptUpdate as EventListener);
    window.addEventListener('isAwaitingPermission', handleAwaitingPermission as EventListener);
    window.addEventListener('onTranscriptStop', handleTranscriptStop as EventListener);
    window.addEventListener(
      'browserSupportsSpeechRecognition',
      handleBrowserSupportsSpeechRecognition as EventListener,
    );
    window.addEventListener('microPhoneAvailable', handleMicrophoneAvailability as EventListener);

    return () => {
      window.removeEventListener('onTranscriptUpdate', handleTranscriptUpdate as EventListener);
      window.removeEventListener('onTranscriptStop', handleTranscriptStop as EventListener);
      window.removeEventListener(
        'browserSupportsSpeechRecognition',
        handleBrowserSupportsSpeechRecognition as EventListener,
      );
      window.removeEventListener(
        'microPhoneAvailable',
        handleMicrophoneAvailability as EventListener,
      );
    };
  }, []);

  const handleCancel = () => {
    const stopEvent = new Event('onCancelListening');
    window.dispatchEvent(stopEvent);
  };

  // Render error messages if browser doesn't support speech recognition or microphone is not available
  if (!browserSupportsSpeechRecognition) {
    return (
      <div
        className={`col d-flex flex-column align-items-center speech-to-text-display ${displayClass}`}
      >
        <div className="speech-to-text-display error text-center">
          Your browser doesn't support microphone access
        </div>
        <ActionButton
          label={'Cancel'}
          action={'default'}
          icon={'fa-solid fa-x fa-sm'}
          onClick={handleCancel}
          extraClass={'border-0 rounded-0'}
        />
      </div>
    );
  }
  if (!isMicrophoneAvailable) {
    return (
      <div
        className={`col d-flex flex-column align-items-center speech-to-text-display ${displayClass}`}
      >
        <div className="row text-center">You must allow microphone access to use this feature</div>
        <div className="row justify-content-center">
          <ActionButton
            label={'Cancel'}
            action={'default'}
            icon={'fa-solid fa-x fa-sm'}
            onClick={handleCancel}
            extraClass={'border-0 rounded-0'}
          />
        </div>
      </div>
    );
  }

  return isListening || transcript ? (
    <div className={`speech-to-text-display ${displayClass}`}>
      <div className="col d-flex flex-column align-items-center">
        <div className="row justify-content-center">
          <div className="icon-container">
            <div className="pulse-ring"></div>
            <Icon icon={'fa-solid fa-microphone fa-xl'} />
          </div>
        </div>
        <div className="row justify-content-center transcript-container">
          <h1>{transcript}</h1>
        </div>
        <ActionButton
          label={'Cancel'}
          action={'default'}
          icon={'fa-solid fa-x fa-sm'}
          onClick={handleCancel}
          extraClass={'border-0 rounded-0'}
        />
      </div>
    </div>
  ) : null;
};

export default TranscriptDisplay;
